import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useMessagesStore } from "@/stores/messages.store";
import {
  MAX_FEEDBACK_COMMENT_LENGTH,
  type ReadMessageFeedbackDto
} from "@package/types";
import { Loader2, ThumbsDown, ThumbsUp } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

type FeedbackRating = ReadMessageFeedbackDto["rating"];

interface MessageFeedbackProps {
  chatId: string;
  messageId: number;
  feedback?: ReadMessageFeedbackDto | null;
}

export function MessageFeedback({
  chatId,
  messageId,
  feedback
}: MessageFeedbackProps) {
  const { t } = useTranslation();
  const setMessageFeedback = useMessagesStore((s) => s.setMessageFeedback);

  const [openRating, setOpenRating] = useState<FeedbackRating | null>(null);
  const [comment, setComment] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const endpoint = `/feedback/chats/${chatId}/messages/${messageId}`;

  const openDialog = (rating: FeedbackRating) => {
    setComment(feedback?.comment ?? "");
    setOpenRating(rating);
  };

  const save = async () => {
    if (!openRating) return;

    setIsSaving(true);
    try {
      const saved = await api.put<ReadMessageFeedbackDto>(endpoint, {
        rating: openRating,
        comment: comment.trim() || null
      });
      setMessageFeedback(chatId, messageId, saved);
      toast.success(t("feedback.saved"));
      setOpenRating(null);
    } catch {
      toast.error(t("feedback.saveError"));
    } finally {
      setIsSaving(false);
    }
  };

  const remove = async () => {
    setIsDeleting(true);
    try {
      await api.delete(endpoint);
      setMessageFeedback(chatId, messageId, null);
      toast.success(t("feedback.deleted"));
      setOpenRating(null);
    } catch {
      toast.error(t("feedback.saveError"));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          "size-6 text-muted-foreground hover:text-foreground",
          feedback?.rating === "POSITIVE" && "text-success"
        )}
        title={t("feedback.rateGood")}
        onClick={() => openDialog("POSITIVE")}
      >
        <ThumbsUp className="size-3" />
        <span className="sr-only">{t("feedback.rateGood")}</span>
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          "size-6 text-muted-foreground hover:text-foreground",
          feedback?.rating === "NEGATIVE" && "text-destructive"
        )}
        title={t("feedback.rateBad")}
        onClick={() => openDialog("NEGATIVE")}
      >
        <ThumbsDown className="size-3" />
        <span className="sr-only">{t("feedback.rateBad")}</span>
      </Button>

      <Dialog
        open={openRating !== null}
        onOpenChange={(open) => !open && setOpenRating(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {openRating === "POSITIVE"
                ? t("feedback.dialogTitleGood")
                : t("feedback.dialogTitleBad")}
            </DialogTitle>
            <DialogDescription>
              {t("feedback.dialogDescription")}
            </DialogDescription>
          </DialogHeader>

          <Textarea
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            maxLength={MAX_FEEDBACK_COMMENT_LENGTH}
            placeholder={t("feedback.commentPlaceholder")}
            className="min-h-32"
          />
          <p className="text-xs text-muted-foreground">
            {comment.trim()
              ? t("feedback.commentUsedHint")
              : t("feedback.commentEmptyHint")}
          </p>

          <DialogFooter className="sm:justify-between">
            {feedback ? (
              <Button
                variant="ghost"
                onClick={remove}
                disabled={isSaving || isDeleting}
                className="text-destructive hover:text-destructive"
              >
                {isDeleting && <Loader2 className="size-4 animate-spin" />}
                {t("feedback.delete")}
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setOpenRating(null)}
                disabled={isSaving || isDeleting}
              >
                {t("header.cancel")}
              </Button>
              <Button onClick={save} disabled={isSaving || isDeleting}>
                {isSaving && <Loader2 className="size-4 animate-spin" />}
                {t("header.save")}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
